/*
 * Ryan O'Dowd
 * 2024-10-10
 *
 * © Copyright 2024 Oakwood Software Consulting, Inc.  All Rights Reserved.
*/
import useTypedSelector from './useTypedSelector';

const fontFamilies = { // @TODO: add these to material/paper theme as well
  primary: 'Ubuntu-Regular',
  primaryBold: 'Ubuntu-Bold',
  primaryItalic: 'Ubuntu-Italic',
  primaryBoldItalic: 'Ubuntu-BoldItalic',
  secondary: 'Montserrat-Regular',
  secondaryBold: 'Montserrat-ExtraBold',
  secondaryItalic: 'Montserrat-Italic',
  secondaryBoldItalic: 'Montserrat-ExtraBoldItalic',
};

export const accentsGreen = { // @TODO: shouldn't be exported (used in index/index.web.tsx right now)
  accentLight: '#e7ff83',
  accent: '#c6ff00',
  accentDark: '#8ab200',
};

const accentsStorm = {
  accentLight: '#82afff',
  accent: '#007aff',
  accentDark: '#004fcc',
};

const test = {
  extraSpelling: '#ff8c64',
  wordsAdded: '#ff02a1',
  spellingPunctuation: '#ffffff',
  wordsMissed: '#ffd770',
  wordsCorrect: '#c6ff00',
};

const stormTheme = {
  white: '#ffffdd',
  primaryExtraLight: '#e3e0e6',
  primaryLight: '#4d5973',
  primary: '#3a465c',
  primaryDark: '#273141',
  primaryExtraDark: '#141922',
  ...accentsStorm,
  icons: '#ffffdd', // @TODO: rename to dialogicon
  lightText: '#ffffdd',
  darkText: '#222222',
  dialogText: '#ffffdd',
  completeBg: '#222222', // @TODO: better color?
  popoverBg: '#222222', // @TODO: better color?
  backgroundImage: require('@assets/img/backgrounds/jungle_blue_dark.png'),
  brickTitle: '#ffffdd',
  incorrect: '#0F4241', // @TODO: update
};

// @TODO: dialog backgrounds should be colored (even if just slightly...and maybe gradient?)
export const themes = { // @TODO: update all of these
  blue: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#dfffff',
      primaryLight: '#33a0c5',
      primary: '#0089ab',
      primaryDark: '#005f86',
      primaryExtraDark: '#002d3d',
      icons: '#002d3d',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#002d3d',
      completeBg: '#444444', // @TODO: better color?
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_blue.png'),
      brickTitle: '#b1dff3',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#53c0e5',
      primaryLight: '#0072a3',
      primary: '#004c7a',
      primaryDark: '#00243f',
      primaryExtraDark: '#001a2d',
      icons: '#001a20',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_blue_dark.png'),
      brickTitle: '#87b5c9',
    },
  },
  storm: {
    isHidden: true,
    fontFamilies,
    light: {...stormTheme, dialogText: '#00373a'},
    dark: stormTheme,
  },
  green: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#d0ffee',
      primaryLight: '#2dbca0',
      primary: '#007e73',
      primaryDark: '#005a52',
      primaryExtraDark: '#00373a',
      icons: '#00373a',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#00373a',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      brickTitle: '#a3f0e2',
      backgroundImage: require('@assets/img/backgrounds/jungle_green.png'),
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#00807d',
      primaryLight: '#006f5f',
      primary: '#004d42',
      primaryDark: '#002f2a',
      primaryExtraDark: '#001f1b',
      icons: '#001f1b',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_green_dark.png'),
      brickTitle: '#7ccdc0',
    },
  },
  purple: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#e3dfff',
      primaryLight: '#8c33c5',
      primary: '#5d00a3',
      primaryDark: '#3f0075',
      primaryExtraDark: '#2a003d',
      icons: '#2a003d',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#2a003d',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_purple.png'),
      brickTitle: '#d1b1f3',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#633387',
      primaryLight: '#5e33a3',
      primary: '#3c008c',
      primaryDark: '#1f004f',
      primaryExtraDark: '#13003a',
      icons: '#13002a',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_purple_dark.png'),
      brickTitle: '#a387d3',
    },
  },
  yellow: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#f0fff0',
      primaryLight: '#d5e533',
      primary: '#b0c200',
      primaryDark: '#7a8a00',
      primaryExtraDark: '#2f3d00',
      icons: '#2f3d00',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#2f3d00',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_yellow.png'),
      brickTitle: '#e1f3b1',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#738700',
      primaryLight: '#627300',
      primary: '#4a5a00',
      primaryDark: '#333f00',
      primaryExtraDark: '#1a1f00',
      icons: '#1a1f00',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_yellow_dark.png'),
      brickTitle: '#b5c987',
    },
  },
  red: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#ffe6f0',
      primaryLight: '#b2264d',
      primary: '#990022',
      primaryDark: '#660017',
      primaryExtraDark: '#330010',
      icons: '#330010',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#330010',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_red.png'),
      brickTitle: '#f3b1c1',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#752244',
      primaryLight: '#8f2d4f',
      primary: '#600020',
      primaryDark: '#300010',
      primaryExtraDark: '#20000d',
      icons: '#10000a',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_red_dark.png'),
      brickTitle: '#c9879b',
    },
  },
  grayscale: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#f2f2f2',
      primaryLight: '#cccccc',
      primary: '#999999',
      primaryDark: '#666666',
      primaryExtraDark: '#333333',
      icons: '#333333',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#333333',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale.png'),
      brickTitle: '#b1b1b1',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#737373',
      primaryLight: '#666666',
      primary: '#4c4c4c',
      primaryDark: '#262626',
      primaryExtraDark: '#1a1a1a',
      icons: '#0d0d0d',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      brickTitle: '#a8a8a8',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale_dark.png'),
    },
  },
};

export default function useTheme() {
  return useTypedSelector((state) => {
    const lightDarkMode = state.settingsLightDarkMode;
    const themeMode = themes[state.theme][lightDarkMode];

    const {backgroundImage, ...themeColors} = themeMode;
    return {
      colors: {
        success: '#477158',
        qqq: '#81cff3', // @TODO: rename (used for passage gradient normalization spectrum and i think something else too)
        incorrect: '#FF4241', // @TODO: update
        incorrectText: '#4422ff', // @TODO: update, especially for dark mode (especially storms)
        inContextHightlight: '#ffccaa',
        badge: '#ff4242', // @TODO: '#c6ff00', // @TODO: '#aaaaff', // @TODO: is this what i want for badge color?
        warnIncorrect: '#FFD700', // @TODO: update
        alert: '#FF4242',
        alertLight: '#FF9292',
        alertDark: '#cF2222',
        white: '#ffffff',
        black: '#000000',
        gray: '#757575',
        lightGray: '#cccccc', // @TODO: copy and call unstarted
        extraLightGray: '#eeeeee',
        disabled: '#bdbdbd',
        bgLight: '#EEEEEE',
        bgDark: '#1b1b1b',
        complete: '#FFD700', // @TODO: rename to inProgress, and have accent color assigned to complete
        darkText: '#212121', // @TODO: this is in theme colors
        myNote: '#ccffdd',
        otherNote: '#bbeeff',
        warningBg: '#af4F70',
        // @TODO: highlighted: '#ffde64',
        test,
        themeColor: state.theme,
        ...accentsGreen,
        ...themeColors,
      },
      fonts: themes[state.theme].fontFamilies,
      backgroundImage,
    };
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));
}
